// @flow
import * as React from 'react';
import { sprintf } from 'sprintf-js';
import styled from 'styled-components';
import type { CurrentUser } from 'api/currentUser';
import { trackClicked } from 'utils/analytics';
import { promoteResumeDatabase } from 'utils/resumeDatabase';
import ResumeDatabaseResponseContainer from 'containers/ResumeDatabaseResponseContainer';
import Box from 'components/Box';
import Divider from 'components/Divider';
import Meter from 'components/Meter';
import { PrimaryButtonRouterLink, LinkButton } from 'components/Button';
import DropdownMenu, {
  DropdownMenuItem,
  DropdownMenuItemRouter,
  DropdownMenuSection,
  DropdownMenuSectionItemRouter,
  DropdownMenuSectionTitle
} from 'components/PageHeaderDropdownMenu';
import { colors, fontWeights } from 'theme';

const NAV_WIDTH = {
  en: 900,
  es: 1050,
  pt: 1000
};

const StyledLinkButton = styled(LinkButton)`
  color: ${colors.battleshipGrey};
  font-size: inherit;
  font-weight: ${fontWeights.normal};
  text-decoration: underline;
`;

const ProfileSection = ({ closeMenu }: { closeMenu: Function }) => (
  <>
    <Divider light my={0} />
    <Box minWidth={200} px={12} pt={16} pb={24} textAlign="center">
      <Box mb="8px" color={colors.battleshipGrey}>
        {getText('Your profile is incomplete')}
      </Box>
      <Meter
        current={1}
        total={3}
        color="turtleGreen"
        delay={0}
        duration={0.5}
      />
      <Box mt={20} mb="8px">
        <PrimaryButtonRouterLink
          to="/dashboard/application-history"
          size="small"
          square
          width="100%"
          onClick={() => {
            closeMenu();
            trackClicked('Add Resume', {
              context: 'Header User Dropdown'
            });
          }}
        >
          {getText('Add Resume')}
        </PrimaryButtonRouterLink>
      </Box>
      <Box width={1} display="flex" flexDirection="column" alignItems="center">
        <ResumeDatabaseResponseContainer>
          {({ dismiss }) => (
            <StyledLinkButton
              onClick={() => {
                dismiss();
                closeMenu();
                trackClicked('Dismiss Resume Database CTA', {
                  context: 'Header User Dropdown'
                });
              }}
            >
              {getText('No, thanks')}
            </StyledLinkButton>
          )}
        </ResumeDatabaseResponseContainer>
      </Box>
    </Box>
  </>
);

const PageHeaderAccountDropdown = ({
  currentUser,
  isStaff
}: {
  currentUser: CurrentUser,
  isStaff: boolean
}) => {
  const promoteResumeDb = promoteResumeDatabase({ currentUser });

  return (
    <DropdownMenu
      aria-label={getText('Account Information')}
      title={() => (
        <Box classes={['flex']}>
          <Box
            display={['none', null, null, 'flex']}
            alignItems="center"
            maxWidth={[
              null,
              null,
              null,
              `calc(100vw - ${NAV_WIDTH[USER_LOCALE]}px)`
            ]}
            minWidth={[null, null, null, 32]}
          >
            {promoteResumeDb && (
              <Box
                display="flex"
                justifyContent="center"
                alignItems="center"
                width="calc(1em + 2px)"
                height="calc(1em + 2px)"
                flex="0 0 auto"
                mr="6px"
                borderRadius="50%"
                color="white"
                bg={colors.waterBlue}
                fontWeight={fontWeights.bold}
                lineHeight={1.375}
                style={{ '-webkit-font-smoothing': 'antialiased' }}
              >
                <Box fontSize="calc(1em - 1px)">!</Box>
              </Box>
            )}

            <Box whiteSpace="noWrap" textOverflow="ellipsis" overflow="hidden">
              {/* TRANSLATORS: user greeting at top of page */ sprintf(
                getText('Hi, %(name)s!'),
                {
                  name: currentUser.firstName || currentUser.email
                }
              )}
            </Box>
          </Box>
        </Box>
      )}
      data-qa-id="nav-dropdown"
    >
      {({ closeMenu }) => (
        <>
          {promoteResumeDb && <ProfileSection closeMenu={closeMenu} />}

          {isStaff ? (
            <DropdownMenuItem href="/dashboard">
              {getText('My Account')}
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItemRouter to="/dashboard">
              {getText('My Account')}
            </DropdownMenuItemRouter>
          )}

          {currentUser.orgs.length > 0 && (
            <DropdownMenuSection>
              <DropdownMenuSectionTitle>
                {getText('My Organizations')}
              </DropdownMenuSectionTitle>
              {currentUser.orgs.map(org =>
                isStaff ? (
                  <DropdownMenuItem
                    key={org.id}
                    href={
                      org.hasGradProgram
                        ? `/admin/${org.id}/grad-program`
                        : `/admin/${org.id}`
                    }
                    onClick={closeMenu}
                  >
                    {org.name}
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuSectionItemRouter
                    key={org.id}
                    to={
                      org.hasGradProgram
                        ? `/admin/${org.id}/grad-program`
                        : `/admin/${org.id}`
                    }
                    onClick={closeMenu}
                  >
                    {org.name}
                  </DropdownMenuSectionItemRouter>
                )
              )}
            </DropdownMenuSection>
          )}

          {currentUser.groups.length > 0 && (
            <DropdownMenuSection>
              <DropdownMenuSectionTitle>
                {getText('My Groups')}
              </DropdownMenuSectionTitle>
              {currentUser.groups.map(group => (
                <DropdownMenuSectionItemRouter
                  key={group.id}
                  to={`/group-admin/${group.id}`}
                  onClick={closeMenu}
                >
                  {group.name}
                </DropdownMenuSectionItemRouter>
              ))}
            </DropdownMenuSection>
          )}

          <DropdownMenuItem href="/logout" data-qa-id="logout-link">
            {getText('Log Out')}
          </DropdownMenuItem>
        </>
      )}
    </DropdownMenu>
  );
};

export default PageHeaderAccountDropdown;
